import { useState, useEffect } from 'react';
import { Link } from '@tanstack/react-router';
import { Button } from '@/components/Button';
import Loading from '@/components/Loading';
import type AgentApiAllianceApplicationsShow from '@/types/serializers/AgentApiAllianceApplicationsShow';
import { MdDescription, MdOpenInNew } from 'react-icons/md';
import { useSetBreadcrumbTitle } from '@/hooks/useBreadcrumb';
import UsersMenu from '@/features/agent/components/UsersMenu';
import useActiveUsers from '@/features/agent/active_users/hooks/useActiveUsers';
import AllianceChatPanel from './AllianceChatPanel';
import AllianceProgressTimeline from './AllianceProgressTimeline';
import AllianceApplicationStatusMenu, {
  type AllianceStatusItem,
} from './AllianceApplicationStatusMenu';
import useUpdateAgentAllianceApplicationMemo from '../api/useUpdateAgentAllianceApplicationMemo';
import useUpdateAllianceCompanyAssignees from '../api/useUpdateAllianceCompanyAssignees';
import { showErrorToast } from '@/features/agent/job_change_processes/lib/toast';
import { getErrorMessage } from '../utils/getErrorMessage';

interface ApplicationDetailProps {
  applicationId: number | string;
  application: AgentApiAllianceApplicationsShow | undefined;
  isLoading: boolean;
  onStatusChange: (status: string) => void;
}

const statusItems: AllianceStatusItem[] = [
  { value: 'applied', label: '応募中' },
  { value: 'document_screening', label: '書類選考中' },
  { value: 'in_progress', label: '選考中' },
  { value: 'offered', label: '内定', tone: 'primary' },
  { value: 'accepted', label: '承諾', tone: 'primary' },
  { value: 'candidate_declined', label: '候補者辞退', tone: 'danger' },
  { value: 'rejected', label: '不採用', tone: 'danger' },
];

const handleDownload = (url: string | null) => {
  if (!url) return;
  window.open(url, '_blank', 'noopener,noreferrer');
};

type TabKey = 'applicant' | 'job';

export default function ApplicationDetail({
  applicationId,
  application,
  isLoading,
  onStatusChange,
}: ApplicationDetailProps) {
  useSetBreadcrumbTitle(application?.candidate_name);

  const [activeTab, setActiveTab] = useState<TabKey>('applicant');
  const [memo, setMemo] = useState(application?.internal_memo ?? '');
  const updateMemo = useUpdateAgentAllianceApplicationMemo();
  const updateCompanyAssignees = useUpdateAllianceCompanyAssignees();
  // 担当者候補はエージェントハブに登録されている自組織ユーザー全てを表示する
  const { data: activeUsersData } = useActiveUsers();
  const activeUsers = activeUsersData?.activeUsers ?? [];

  useEffect(() => {
    setMemo(application?.internal_memo ?? '');
  }, [applicationId, application?.internal_memo]);

  if (isLoading || !application) {
    return <Loading />;
  }

  const company = application.company;
  const companyAssigneeIds = company?.organization_users.map((user) => user.id) ?? [];

  const handleSelectAssignees = (companyId: number, userIds: number[]) => {
    updateCompanyAssignees.mutate(
      { companyId, organizationUserIds: userIds },
      {
        onError: (error) => {
          showErrorToast(getErrorMessage(error, '担当者の更新に失敗しました'));
        },
      }
    );
  };

  const handleMemoBlur = () => {
    if (memo === (application.internal_memo ?? '')) return;
    updateMemo.mutate(
      { id: String(applicationId), internalMemo: memo },
      {
        // 保存失敗が無言だと「保存できた」と誤認しやすいため、
        // 他の Alliance 画面と同様に toast でエラーを通知する。
        onError: (error) => {
          showErrorToast(getErrorMessage(error, '社内メモの更新に失敗しました'));
        },
      }
    );
  };

  return (
    <div className="flex flex-col gap-md">
      <div className="flex w-full flex-col gap-lg rounded bg-white p-lg shadow-layer1">
        <div className="flex items-center gap-md">
          <div className="flex items-center gap-xs">
            <h2 className="text-heading-md font-bold">{application.candidate_name}</h2>
            <span className="text-heading-md font-bold text-sub">×</span>
            <h2 className="text-heading-md font-bold">{application.company?.name}</h2>
          </div>
          {company && (
            <UsersMenu
              activeUsers={activeUsers}
              selectedUserIds={companyAssigneeIds}
              onSelect={(userIds) => handleSelectAssignees(company.id, userIds)}
              emptyLabel="未設定"
            />
          )}
        </div>

        <div className="flex items-center justify-between gap-md">
          <div className="flex items-center gap-sm">
            <p className="text-heading-3xs font-bold text-sub">ステータス</p>
            <div className="w-[200px]">
              <AllianceApplicationStatusMenu
                items={statusItems}
                value={application.status}
                onChange={onStatusChange}
              />
            </div>
          </div>
          <AllianceProgressTimeline status={application.status} />
        </div>

        <div className="flex justify-between gap-md border-t border-solid border-border pt-md">
          <div className="flex flex-1 flex-col gap-xs">
            <p className="text-heading-3xs font-bold text-sub">社内メモ</p>
            <textarea
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              onBlur={handleMemoBlur}
              placeholder="社内メモを入力..."
              rows={3}
              className="w-full resize-none rounded border border-solid border-border p-sm text-body-sm outline-hidden focus:border-primary"
            />
          </div>
          <div className="flex flex-1 flex-col gap-xs">
            <p className="text-heading-3xs font-bold text-sub">添付ファイル</p>
            {application.documents.length > 0 ? (
              <div className="flex flex-wrap gap-sm">
                {application.documents.map((doc) => (
                  <Button
                    key={doc.id}
                    color="secondary"
                    size="sm"
                    onClick={() => handleDownload(doc.url)}
                    className="flex max-w-[380px] min-w-0 items-center gap-xs"
                    title={doc.filename}
                  >
                    <MdDescription className="size-5 shrink-0" />
                    <span className="min-w-0 truncate">{doc.filename}</span>
                  </Button>
                ))}
              </div>
            ) : (
              <p className="text-body-sm text-sub">添付ファイルなし</p>
            )}
          </div>
        </div>
      </div>

      <div className="flex h-[calc(100vh-156px)] items-stretch gap-md">
        <div className="flex flex-1 flex-col overflow-hidden rounded bg-white shadow-layer1">
          <div className="flex border-b border-solid border-border">
            <button
              type="button"
              onClick={() => setActiveTab('applicant')}
              className={`flex-1 border-0 border-b-2 border-solid bg-transparent px-lg py-sm text-body-sm ${
                activeTab === 'applicant'
                  ? 'border-b-primary font-bold text-primary'
                  : 'border-b-transparent text-main'
              }`}
            >
              応募者情報
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('job')}
              className={`flex-1 border-0 border-b-2 border-solid bg-transparent px-lg py-sm text-body-sm ${
                activeTab === 'job'
                  ? 'border-b-primary font-bold text-primary'
                  : 'border-b-transparent text-main'
              }`}
            >
              求人情報
            </button>
          </div>

          <div className="flex flex-col gap-md overflow-y-auto p-lg">
            {activeTab === 'applicant' ? (
              <>
                <div className="flex flex-col gap-sm">
                  <h3 className="text-heading-xxs font-bold">基本情報</h3>
                  <div className="flex flex-col gap-xs">
                    {application.candidate_field_values.map((field) => (
                      <div key={field.form_field_id} className="flex gap-md">
                        <p className="w-[145px] shrink-0 text-body-sm text-sub">
                          {field.field_name}
                        </p>
                        <p className="text-body-sm break-words whitespace-pre-wrap">
                          {field.value || '-'}
                        </p>
                      </div>
                    ))}
                  </div>
                  {application.resume_url && (
                    <div className="flex gap-md">
                      <p className="w-[145px] shrink-0 text-body-sm text-sub">履歴書</p>
                      <Button
                        type="button"
                        color="secondary"
                        size="md"
                        onClick={() => handleDownload(application.resume_url)}
                        className="flex w-fit max-w-[380px] min-w-0 items-center justify-start gap-xs"
                        title={application.resume_filename || '履歴書'}
                      >
                        <MdDescription className="size-5 shrink-0" />
                        <span className="min-w-0 truncate">
                          {application.resume_filename || '履歴書'}
                        </span>
                      </Button>
                    </div>
                  )}
                  {application.cv_url && (
                    <div className="flex gap-md">
                      <p className="w-[145px] shrink-0 text-body-sm text-sub">職務経歴書</p>
                      <Button
                        type="button"
                        color="secondary"
                        size="md"
                        onClick={() => handleDownload(application.cv_url)}
                        className="flex w-fit max-w-[380px] min-w-0 items-center justify-start gap-xs"
                        title={application.cv_filename || '職務経歴書'}
                      >
                        <MdDescription className="size-5 shrink-0" />
                        <span className="min-w-0 truncate">
                          {application.cv_filename || '職務経歴書'}
                        </span>
                      </Button>
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-sm border-t border-solid border-border pt-md">
                  <h3 className="text-heading-xxs font-bold">提供元エージェント</h3>
                  <div className="flex gap-md">
                    <p className="w-[145px] shrink-0 text-body-sm text-sub">会社名</p>
                    <p className="text-body-sm">{application.agent_name}</p>
                  </div>
                  <div className="flex gap-md">
                    <p className="w-[145px] shrink-0 text-body-sm text-sub">メールアドレス</p>
                    <p className="text-body-sm break-all">{application.agent_email}</p>
                  </div>
                </div>

                {application.note && (
                  <div className="flex flex-col gap-sm border-t border-solid border-border pt-md">
                    <h3 className="text-heading-xxs font-bold">推薦文</h3>
                    <p className="text-body-sm break-words whitespace-pre-wrap">
                      {application.note}
                    </p>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="flex flex-col gap-xxs">
                  {application.job_title && (
                    <Link
                      className="flex min-w-0 cursor-pointer items-center gap-xxs border-none bg-transparent p-0 text-heading-xxs font-bold text-primary underline"
                      to="/agent/provided_job_posts/$providedJobPostsId"
                      params={{ providedJobPostsId: String(application.provided_job_post_id) }}
                    >
                      <span className="truncate">{application.job_title}</span>
                      <MdOpenInNew className="size-4 shrink-0" />
                    </Link>
                  )}
                  <div className="flex items-center gap-xxs">
                    <p className="max-w-73.75 min-w-0 truncate text-body-xs text-main">
                      {application.company?.name ?? ''}
                    </p>
                    <span className="shrink-0 text-body-sm text-main">/</span>
                    <p className="shrink-0 text-body-xs text-main">
                      {application.job_position_name ?? ''}
                    </p>
                  </div>
                </div>

                {application.mandatory_requirement && (
                  <div className="flex flex-col gap-sm border-t border-solid border-border pt-md">
                    <h3 className="text-heading-xxs font-bold">必須要件</h3>
                    <p className="text-body-sm break-words whitespace-pre-wrap">
                      {application.mandatory_requirement}
                    </p>
                  </div>
                )}

                {application.job_content_json.map((item) => (
                  <div
                    key={item.key}
                    className="flex flex-col gap-sm border-t border-solid border-border pt-md"
                  >
                    <h3 className="text-heading-xxs font-bold">{item.key}</h3>
                    <p className="text-body-sm break-words whitespace-pre-wrap">{item.content}</p>
                  </div>
                ))}

                {(application.salary_text || application.working_location) && (
                  <div className="flex flex-col gap-sm border-t border-solid border-border pt-md">
                    {application.salary_text && (
                      <div className="flex gap-md">
                        <p className="w-[145px] shrink-0 text-body-sm text-sub">給与</p>
                        <p className="text-body-sm break-words whitespace-pre-wrap">
                          {application.salary_text}
                        </p>
                      </div>
                    )}
                    {application.working_location && (
                      <div className="flex gap-md">
                        <p className="w-[145px] shrink-0 text-body-sm text-sub">勤務地</p>
                        <p className="text-body-sm break-words whitespace-pre-wrap">
                          {application.working_location}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <AllianceChatPanel
          applicationId={String(applicationId)}
          agentName={application.agent_name}
        />
      </div>
    </div>
  );
}
